import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { healthDevicesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Warning, WarningOctagon, Check, Heartbeat } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LABELS = {
  glucose: 'Glucose',
  blood_pressure_systolic: 'Blood pressure',
  heart_rate: 'Heart rate',
  temperature: 'Temperature',
  blood_oxygen: 'Blood oxygen',
};

const formatValue = (a) => `${a.value}${a.secondary_value ? `/${a.secondary_value}` : ''} ${a.unit || ''}`;

const AlertRow = ({ alert, onAck, onOpen }) => {
  const urgent = alert.level === 'urgent';
  return (
    <div
      data-testid={`health-alert-${alert.id}`}
      className={`flex items-center justify-between gap-4 p-3 rounded-lg border ${urgent ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}
    >
      <button type="button" className="flex items-center gap-3 text-left" onClick={onOpen} data-testid={`health-alert-open-${alert.id}`}>
        {urgent ? <WarningOctagon size={22} weight="fill" className="text-red-600 shrink-0" /> : <Warning size={22} weight="fill" className="text-amber-500 shrink-0" />}
        <div>
          <p className="font-semibold text-slate-900">{alert.patient_name}</p>
          <p className={`text-sm ${urgent ? 'text-red-700' : 'text-amber-700'}`}>
            {alert.message}: {LABELS[alert.data_type] || alert.data_type} {formatValue(alert)}
          </p>
          <p className="text-xs text-slate-500">{new Date(alert.recorded_at).toLocaleString()} • {alert.provider}</p>
        </div>
      </button>
      <Button size="sm" variant="outline" onClick={onAck} data-testid={`health-alert-ack-${alert.id}`}>
        <Check size={14} className="mr-1" /> Acknowledge
      </Button>
    </div>
  );
};

export const HealthAlertsPanel = ({ detailPath }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ alerts: [], urgent_count: 0, watch_count: 0 });
  const [showWatch, setShowWatch] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await healthDevicesAPI.getAlerts('open');
      setData(res.data);
    } catch (error) {
      console.error('Error loading health alerts:', error);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (id) => {
    try {
      await healthDevicesAPI.acknowledgeAlert(id);
      toast.success('Alert acknowledged');
      load();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  if (data.alerts.length === 0) return null;
  const urgent = data.alerts.filter(a => a.level === 'urgent');
  const watch = data.alerts.filter(a => a.level === 'watch');
  const open = () => detailPath && navigate(detailPath);

  return (
    <div className="mb-8 bg-white rounded-xl border border-slate-200 p-5" data-testid="health-alerts-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heartbeat size={22} className="text-red-600" />
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Patient Reading Alerts</h2>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-red-100 text-red-800" data-testid="health-alerts-urgent-count">{data.urgent_count} urgent</Badge>
          <Badge className="bg-amber-100 text-amber-800" data-testid="health-alerts-watch-count">{data.watch_count} to watch</Badge>
        </div>
      </div>
      <div className="space-y-2">
        {urgent.map(a => <AlertRow key={a.id} alert={a} onAck={() => acknowledge(a.id)} onOpen={open} />)}
        {urgent.length === 0 && <p className="text-sm text-slate-500" data-testid="health-alerts-no-urgent">No urgent readings.</p>}
      </div>
      {watch.length > 0 && (
        <div className="mt-4">
          <Button variant="ghost" size="sm" onClick={() => setShowWatch(!showWatch)} data-testid="health-alerts-toggle-watch">
            {showWatch ? 'Hide' : 'Show'} {watch.length} reading{watch.length > 1 ? 's' : ''} outside normal range
          </Button>
          {showWatch && (
            <div className="space-y-2 mt-2">
              {watch.map(a => <AlertRow key={a.id} alert={a} onAck={() => acknowledge(a.id)} onOpen={open} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
